import React, { useState, useEffect } from 'react';
import api from '../services/api';

const PERIODS = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
];

function Reports() {
  const [summary, setSummary] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [topClients, setTopClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    fetchReports();
  }, [period]);

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      const [summaryData, revenueData, clientsData] = await Promise.all([
        api.getReportSummary(),
        api.getReportRevenue(period),
        api.getReportClients(period),
      ]);
      setSummary(summaryData.summary);
      setRevenue(revenueData);
      setTopClients(clientsData.top_clients || []);
    } catch (err) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value?.toFixed(1) || 0}%`;
  };

  if (loading && !summary) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-primary mb-2">Reports</h1>
            <p className="text-slate-600">Track your business performance and revenue</p>
          </div>
          <div className="mt-4 md:mt-0">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-4 py-2 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition bg-white font-medium"
            >
              {PERIODS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center">
            <i className="fas fa-exclamation-circle mr-2"></i>
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
          {/* Revenue Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="fas fa-dollar-sign text-green-600 text-xl"></i>
              </div>
              {summary?.revenue_change_percent !== 0 && (
                <span className={`text-sm font-medium ${summary?.revenue_change_percent > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatPercent(summary?.revenue_change_percent)}
                </span>
              )}
            </div>
            <div className="text-3xl font-black text-primary mb-1">
              {formatCurrency(summary?.this_month_revenue)}
            </div>
            <div className="text-slate-500 text-sm">This Month Revenue</div>
          </div>

          {/* Outstanding Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <i className="fas fa-clock text-yellow-600 text-xl"></i>
              </div>
            </div>
            <div className="text-3xl font-black text-primary mb-1">
              {formatCurrency(summary?.outstanding_amount)}
            </div>
            <div className="text-slate-500 text-sm">Outstanding Invoices</div>
          </div>

          {/* Jobs Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="fas fa-briefcase text-blue-600 text-xl"></i>
              </div>
            </div>
            <div className="text-3xl font-black text-primary mb-1">
              {summary?.active_jobs || 0}
            </div>
            <div className="text-slate-500 text-sm">Active Jobs</div>
          </div>

          {/* Quote Conversion Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="fas fa-percentage text-purple-600 text-xl"></i>
              </div>
            </div>
            <div className="text-3xl font-black text-primary mb-1">
              {summary?.quote_conversion_rate || 0}%
            </div>
            <div className="text-slate-500 text-sm">Quote Conversion Rate</div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid gap-6 lg:grid-cols-2 mb-8">
          {/* Revenue Chart */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-primary mb-6">Revenue Trend</h2>
            {revenue?.revenue_by_month?.length > 0 ? (
              <div className="space-y-4">
                {revenue.revenue_by_month.map((item, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-20 text-sm text-slate-500 font-medium">
                      {item.month}
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-slate-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-secondary to-orange-400 h-full rounded-full"
                          style={{
                            width: `${Math.min((item.total / (revenue.total_revenue || 1)) * 100 * revenue.revenue_by_month.length, 100)}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="w-24 text-right font-bold text-primary">
                      {formatCurrency(item.total)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <i className="fas fa-chart-line text-4xl text-slate-300 mb-4"></i>
                <p>No revenue data for this period</p>
              </div>
            )}
            <div className="mt-6 pt-4 border-t border-slate-100">
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Total Revenue</span>
                <span className="text-xl font-bold text-primary">
                  {formatCurrency(revenue?.total_revenue)}
                </span>
              </div>
            </div>
          </div>

          {/* Top Clients */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-primary mb-6">Top Clients</h2>
            {topClients.length > 0 ? (
              <div className="space-y-4">
                {topClients.slice(0, 5).map((client, index) => (
                  <div key={client.id} className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold mr-4">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-primary truncate">{client.name}</div>
                      <div className="text-sm text-slate-500">
                        {client.job_count} jobs • {client.invoice_count} invoices
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-primary">
                        {formatCurrency(client.total_revenue)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <i className="fas fa-users text-4xl text-slate-300 mb-4"></i>
                <p>No client revenue data for this period</p>
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-primary mb-6">Business Overview</h2>
          <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-6">
            <div className="text-center">
              <div className="text-3xl font-black text-primary">{summary?.total_jobs || 0}</div>
              <div className="text-slate-500 text-sm">Total Jobs</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-primary">{summary?.completed_jobs || 0}</div>
              <div className="text-slate-500 text-sm">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-primary">{summary?.quotes_sent || 0}</div>
              <div className="text-slate-500 text-sm">Quotes Sent</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-primary">{summary?.quotes_accepted || 0}</div>
              <div className="text-slate-500 text-sm">Quotes Accepted</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-primary">{summary?.total_clients || 0}</div>
              <div className="text-slate-500 text-sm">Total Clients</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-primary">{summary?.active_clients || 0}</div>
              <div className="text-slate-500 text-sm">Active Clients</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Reports;
